import { NavLink } from "react-router-dom";
import { useState } from "react";
import { LayoutDashboard, Truck, ClipboardList, Shield, ShieldOff, Settings, History, Warehouse, Boxes, Mail, HelpCircle, Calculator, FileSpreadsheet } from "lucide-react";
import { cn } from "../lib/utils";
import { useAuth } from "../lib/auth";
import { salvageLabel, useBranding } from "../lib/company";
import { hasCap } from "../lib/roles";
import { SupportDialog } from "./SupportDialog";
import { useIntakeDialog } from "../context/IntakeDialogContext";

// Roles:
//  - admin: sees everything (NAV + ADMIN_NAV including Fleet management)
//  - dispatcher: sees Dashboard / Jobs Queue / Impounds / Impound History / History.
//  - viewer (billing role): History + Impounds + Impound History (live read-only).
//
// Feb 2026 email-intake refit — the "Email Intake" row is no longer a
// route (`/intake-inbox` page nuked). It now opens the manual
// paste-and-parse dialog via the IntakeDialogContext. Auto-ingested
// allowlisted emails skip the dialog entirely and land straight in the
// jobs queue.
const NAV = [
  { to: "/", label: "Dashboard", icon: LayoutDashboard, testid: "nav-dashboard" },
  { to: "/jobs", label: "Jobs Queue", icon: ClipboardList, testid: "nav-jobs" },
  { action: "intake-dialog", label: "Email Intake", icon: Mail, testid: "nav-intake-inbox" },
  { to: "/yards", label: "Storage Yards", icon: Boxes, testid: "nav-yards" },
  { to: "/impounds", label: "Impounds", icon: Shield, testid: "nav-impounds" },
  { to: "/impound-history", label: "Impound History", icon: ShieldOff, testid: "nav-impound-history" },
  { to: "/owned-in-lieu", label: "Owned in Lieu", icon: Warehouse, testid: "nav-nts-impounds" },
  { to: "/history", label: "History", icon: History, testid: "nav-history" },
  { to: "/invoicing", label: "Invoicing", icon: FileSpreadsheet, testid: "nav-invoicing" },
  { to: "/tow-calc", label: "Tow Calc", icon: Calculator, testid: "nav-tow-calc" },
];

const ADMIN_NAV = [
  { to: "/fleet", label: "Fleet", icon: Truck, testid: "nav-fleet" },
  { to: "/settings", label: "Integrations", icon: Settings, testid: "nav-settings" },
];

export default function Sidebar() {
  const { user, isAdmin } = useAuth();
  const branding = useBranding();
  const [supportOpen, setSupportOpen] = useState(false);
  const canSeeSupport = hasCap(user, "admin", "dispatcher");
  const { open: openIntakeDialog } = useIntakeDialog();
  const sname = branding?.salvage_entity_name || salvageLabel();
  // Sep 2026 — viewer role retired (Nuclear commit). Everyone in the
  // back office sees the full nav; admin extras stay gated below.
  const visibleNav = NAV;

  const linkClasses = ({ isActive }) =>
    cn(
      "flex items-center gap-3 px-3 py-2.5 rounded-md text-sm font-semibold transition-colors w-full text-left",
      isActive
        ? "bg-blue-600 text-white"
        : "text-slate-400 hover:bg-slate-900 hover:text-white"
    );

  return (
    <aside
      className="hidden md:flex w-56 xl:w-64 shrink-0 flex-col bg-slate-950 text-slate-200 border-r border-slate-900 sticky top-0 h-screen"
      data-testid="app-sidebar"
    >
      <div className="px-4 xl:px-6 py-4 xl:py-6 border-b border-slate-900">
        <div className="flex items-center gap-3">
          <img
            src="/branding/tow-hook-icon-1024.png"
            alt="Tow-Hook"
            className="h-9 w-9 rounded-md shrink-0"
            data-testid="sidebar-brand-mark"
          />
          <div className="min-w-0">
            <div className="font-display font-black text-white text-base leading-tight" data-testid="sidebar-brand-name">
              {branding?.brand_name || "\u00A0"}
            </div>
            <div className="uppercase-label text-slate-500 mt-1" style={{ color: "#64748B" }}>
              {branding?.brand_tagline || "\u00A0"}
            </div>
          </div>
        </div>
      </div>

      <nav className="flex-1 px-3 py-4 space-y-1">
        {visibleNav.map((item) => {
          const Icon = item.icon;
          const label = (item.label || "").replace(/NTS/g, sname);
          if (item.action === "intake-dialog") {
            return (
              <button
                key={item.testid}
                type="button"
                onClick={openIntakeDialog}
                data-testid={item.testid}
                className={linkClasses({ isActive: false })}
              >
                <Icon className="h-4 w-4" strokeWidth={2.2} />
                <span className="flex-1">{label}</span>
              </button>
            );
          }
          return (
            <NavLink
              key={item.to}
              to={item.to}
              end={item.to === "/"}
              data-testid={item.testid}
              className={linkClasses}
            >
              <Icon className="h-4 w-4" strokeWidth={2.2} />
              <span className="flex-1">{label}</span>
            </NavLink>
          );
        })}

        {isAdmin && (
          <>
            <div className="pt-4 pb-1 px-3 uppercase-label text-slate-600">Admin</div>
            {ADMIN_NAV.map((item) => {
              const Icon = item.icon;
              return (
                <NavLink
                  key={item.to}
                  to={item.to}
                  data-testid={item.testid}
                  className={linkClasses}
                >
                  <Icon className="h-4 w-4" strokeWidth={2.2} />
                  {(item.label || "").replace(/NTS/g, sname)}
                </NavLink>
              );
            })}
          </>
        )}
      </nav>

      <div className="px-3 py-3 border-t border-slate-900">
        {/* Feb 2026 — Tow-Hook SaaS branding anchored to the very bottom of
            the sidebar. Click opens the marketing site (tow-hook.com) in a
            new tab. The user avatar + logout used to live here — they've
            moved up into the top bar so the brand block gets full billing.
            Oct 2026 — a "Need help?" link sits right beneath the brand for
            admins + dispatchers so they can email support without leaving
            the app. */}
        <a
          href="https://tow-hook.com"
          target="_blank"
          rel="noopener noreferrer"
          className="flex items-center gap-3 px-2 py-2 rounded-md hover:bg-slate-900 transition-colors group"
          data-testid="sidebar-tow-hook-brand"
          title="Tow-Hook.com"
        >
          <img
            src="/branding/tow-hook-icon-1024.png"
            alt="Tow-Hook"
            className="h-11 w-11 rounded-lg shrink-0 group-hover:scale-105 transition-transform"
          />
          <div className="min-w-0">
            <div className="font-display font-black text-white text-lg leading-none tracking-tight">
              Tow-Hook
            </div>
            <div className="text-[10px] uppercase tracking-widest text-slate-500 mt-1 group-hover:text-blue-400 transition-colors">
              tow-hook.com
            </div>
          </div>
        </a>
        {canSeeSupport && (
          <button
            type="button"
            onClick={() => setSupportOpen(true)}
            className="mt-2 w-full flex items-center gap-2 px-2 py-1.5 rounded-md text-xs text-slate-400 hover:text-blue-400 hover:bg-slate-900 transition-colors"
            data-testid="sidebar-support-btn"
          >
            <HelpCircle className="h-3.5 w-3.5" />
            <span>Need help? Email support</span>
          </button>
        )}
      </div>
      {canSeeSupport && (
        <SupportDialog open={supportOpen} onOpenChange={setSupportOpen} />
      )}
    </aside>
  );
}
